import { ComponentConfig } from '@/components/componentRegistry';
import { Theme } from '@mui/material/styles';
import { Option } from './options';
import { getLoanTypeConfig } from './loanTypeConfig';
import { getPropertyStatusConfig } from './propertyStatusConfig';
import { getCoBorrowerConfig } from './coBorrowerConfig';
import { getActionButtonsConfig } from './actionButtonsConfig';
import { LoanPurpose, LoanType } from '@/config/enums';

interface LoanSetupConfigParams {
  theme: Theme;
  t: (key: string) => string;
  loanOptions: Option[];
  propertyOptions: Option[];
  coBorrowerOptions: Option[];
  selectedLoanType: string | null;
  selectedPropertyStatus: string | null;
  selectedCoBorrower: string | null;
  onSelectLoanType: (value: string) => void;
  onSelectPropertyStatus: (value: string) => void;
  onSelectCoBorrower: (value: string) => void;
  onBack: () => void;
  onNext: () => void;
}

/**
 * Main configuration for LoanSetup page
 */
export const getLoanSetupConfig = ({
  theme,
  t,
  loanOptions,
  propertyOptions,
  coBorrowerOptions,
  selectedLoanType,
  selectedPropertyStatus,
  selectedCoBorrower,
  onSelectLoanType,
  onSelectPropertyStatus,
  onSelectCoBorrower,
  onBack,
  onNext,
}: LoanSetupConfigParams): ComponentConfig => {
  // Build children array conditionally
  const children: (ComponentConfig | null)[] = [
    // Loan Type Section
    getLoanTypeConfig({
      theme,
      t,
      loanOptions,
      selectedLoanType,
      onSelectLoanType,
    }),

    // Property Status Section (only show for purchase)
    selectedLoanType === LoanPurpose.Purchase
      ? getPropertyStatusConfig({
          theme,
          t,
          propertyOptions,
          selectedPropertyStatus,
          onSelectPropertyStatus,
        })
      : null,

    // Co-Borrower Section (only show after property status is selected for purchase, or after loan type is selected for refinance)
    (selectedLoanType === LoanPurpose.Purchase && selectedPropertyStatus) ||
    selectedLoanType === LoanPurpose.RefinanceCashOut
      ? getCoBorrowerConfig({
          theme,
          t,
          coBorrowerOptions,
          selectedCoBorrower,
          onSelectCoBorrower,
        })
      : null,

    // Action Buttons
    getActionButtonsConfig({
      theme,
      t,
      selectedLoanType,
      selectedPropertyStatus,
      selectedCoBorrower,
      onBack,
      onNext,
    }),
  ];

  return {
    component: 'Box',
    props: {
      sx: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        paddingBottom: { xs: theme.unit(4), md: theme.unit(6) },
        paddingX: { xs: theme.unit(3), md: 0 },
      },
    },
    children: [
      {
        component: 'Card',
        props: {
          elevation: 0,
          sx: {
            width: '100%',
            maxWidth: { xs: '100%', sm: '90%', md: '877px', lg: '877px' },
            padding: {
              xs: theme.unit(6),
              sm: theme.unit(8),
              md: theme.unit(12),
            },
            paddingX: {
              xs: theme.unit(4),
              sm: theme.unit(8),
              md: theme.unit(16),
            },
            borderRadius: theme.unit(2.5),
            backgroundColor:
              theme.palette.background?.paper || theme.palette.M3?.white,
            boxShadow: '0px 2px 6px 2px #00000014, 0px 1px 2px 0px #00000014',
            transition: 'all 0.3s ease-in-out',
            marginBottom: { xs: theme.unit(6), md: theme.unit(12) },
          },
        },
        children: [
          {
            component: 'ColumnLayout',
            props: {
              spacing: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              fullWidth: true,
              sx: {
                padding: 0,
                gap: {
                  xs: theme.unit(8),
                  sm: theme.unit(10),
                  md: theme.unit(12),
                },
              },
            },
            children: children.filter((child) => child !== null),
          },
        ],
      },
    ],
  };
};
